import dayjs from "dayjs";
import type { SubscriptionModel } from "@/features/common/api/useSubscription";
import type { PlanModel } from "@/features/subscriptions/api/models/PlanModel";

export type PlanPreference = "Standard" | "ClientCredentials";

export type SubscribeMode = "Subscribe" | "ChangePlan" | "Resume";

export interface SubscribeModalCopy {
	readonly title: string;
	readonly description: string;
	readonly submitText: string;
	readonly successText: string;
}

export const formatSubscriptionDate = (date: Date | string): string => {
	return dayjs(date).format("DD MMM YYYY");
};

export const isAutoRenewEnabled = (subscription: SubscriptionModel): boolean => {
	return !!subscription.autoRenewPaymentCardId || !!subscription.autoRenewPlanId;
};

export const getSubscribeMode = (subscription: SubscriptionModel): SubscribeMode => {
	if (isAutoRenewEnabled(subscription)) {
		return "ChangePlan";
	}

	return subscription.activeSubscription ? "Resume" : "Subscribe";
};

export const canCancelSubscription = (subscription: SubscriptionModel, wasSubscribedOnMount: boolean | null): boolean => {
	return !!subscription.autoRenewPaymentCardId
		&& !!subscription.autoRenewPlanId
		&& !!subscription.autoRenewPlanName
		&& wasSubscribedOnMount === true;
};

export const hasAutoRenewPaymentFailure = (subscription: SubscriptionModel): boolean => {
	return subscription.autoRenewFailureCount > 0 && isAutoRenewEnabled(subscription);
};

export const isFreeTrialAvailable = (subscription: SubscriptionModel, freeTrialPromoCode: string | undefined): boolean => {
	return !!freeTrialPromoCode && !subscription.hasPreviousSubscription && !subscription.activeSubscription;
};

export const getCurrentPlanId = (subscription: SubscriptionModel): string | null => {
	return subscription.autoRenewPlanId ?? subscription.activeSubscription?.planId ?? null;
};

export const getCurrentPlanPreference = (subscription: SubscriptionModel): PlanPreference => {
	return subscription.activeSubscription?.clientCredentialsAccess ? "ClientCredentials" : "Standard";
};

export const getCurrentPaymentCardId = (subscription: SubscriptionModel): string | null => {
	return subscription.autoRenewPaymentCardId ?? subscription.activeSubscription?.paymentCardId ?? null;
};

export const getInitialPlan = (plans: PlanModel[], planPreference: PlanPreference, preselectedPlanId: string | null): PlanModel | null => {
	const preselectedPlan = plans.find(x => x.planId === preselectedPlanId);
	if (preselectedPlan) {
		return preselectedPlan;
	}

	const preferredPlan = plans.find(x => planPreference === "ClientCredentials" ? x.clientCredentialsAccess : !x.clientCredentialsAccess);
	return preferredPlan ?? plans.at(0) ?? null;
};

export const isSubscribeSelectionUnchanged = (subscription: SubscriptionModel, planId: string, paymentCardId: string, promoCode: string | null): boolean => {
	return promoCode === null
		&& planId === subscription.autoRenewPlanId
		&& paymentCardId === subscription.autoRenewPaymentCardId;
};

export const getSubscribeModalCopy = (mode: SubscribeMode, subscription: SubscriptionModel): SubscribeModalCopy => {
	switch (mode) {
	case "Subscribe":
		return {
			title: "Subscribe",
			description: "Choose a plan and payment card to start your subscription.",
			submitText: "Start Subscription",
			successText: "Subscription started"
		};
	case "ChangePlan":
		return {
			title: "Change Plan",
			description: subscription.activeSubscription
				? `Your new plan and card will apply from your next renewal on ${formatSubscriptionDate(subscription.activeSubscription.expiryDate)}. You won't be charged now.`
				: "Your new plan and card will be used for your next renewal attempt. You won't be charged now.",
			submitText: "Update Plan",
			successText: "Plan updated"
		};
	case "Resume":
		return {
			title: "Resume Subscription",
			description: "Choose a plan and payment card. You'll be charged today and a new subscription will start immediately.",
			submitText: "Resume Subscription",
			successText: "Subscription resumed"
		};
	}
};
